import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaChevronDown, FaChevronUp, FaEdit, FaSave, FaTimesCircle, FaTrashAlt } from 'react-icons/fa';
import SelectSearchInput from '../../SelectSearch';
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import Footer from '../../Footer';

const ViewSubRecipe = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [subRecipes, setSubRecipes] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editedRecipe, setEditedRecipe] = useState(null);
    const [editedItems, setEditedItems] = useState([]);

    const kgVariations = useMemo(() => ['kg', 'kgs', 'kilogram', 'kilograms'], []);
    const gramVariations = useMemo(() => ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'], []);
    const literVariations = useMemo(() => ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'], []);
    const milliliterVariations = useMemo(() => ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls'], []);

    const fetchSubRecipes = async () => {
        if (!token || !selectedOutlet) return;

        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/sub-recipes`, {
                params: {
                    token: token,
                    outlet: selectedOutlet
                }
            });
            setSubRecipes(response.data);
        } catch (error) {
            console.error('Error fetching sub-recipes:', error);
            toast.error('Failed to fetch sub-recipes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token)
            fetchSubRecipes();
    }, [token, selectedOutlet, baseUrl]);

    const toggleRow = (id) => {
        const newExpandedRows = [...expandedRows];
        const index = newExpandedRows.indexOf(id);

        if (index === -1) {
            newExpandedRows.push(id);
        } else {
            newExpandedRows.splice(index, 1);
        }

        setExpandedRows(newExpandedRows);
    };

    const isRowExpanded = (id) => {
        return expandedRows.includes(id);
    };

    const convertToBaseUnit = (quantity, uom, new_uom) => {
        const qty = parseFloat(quantity);
        if (isNaN(qty)) return 0;

        if (!new_uom || uom.toLowerCase() === new_uom.toLowerCase()) return qty;

        if (kgVariations.includes(uom.toLowerCase()) && gramVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000;
        }
        if (gramVariations.includes(uom.toLowerCase()) && kgVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000;
        }

        if (literVariations.includes(uom.toLowerCase()) && milliliterVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000;
        }
        if (milliliterVariations.includes(uom.toLowerCase()) && literVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000;
        }

        return qty;
    };

    const convertToDisplayUnit = (quantity, uom, new_uom) => {
        const qty = parseFloat(quantity);
        if (isNaN(qty)) return 0;

        if (!new_uom || uom.toLowerCase() === new_uom.toLowerCase()) return qty;

        if (kgVariations.includes(uom.toLowerCase()) && gramVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000;
        }
        if (gramVariations.includes(uom.toLowerCase()) && kgVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000;
        }

        if (literVariations.includes(uom.toLowerCase()) && milliliterVariations.includes(new_uom.toLowerCase())) {
            return qty * 1000;
        }
        if (milliliterVariations.includes(uom.toLowerCase()) && literVariations.includes(new_uom.toLowerCase())) {
            return qty / 1000;
        }

        return qty;
    };

    const handleEdit = (recipe) => {
        setEditingId(recipe.id);
        setEditedRecipe({
            ...recipe,
            costprice: parseFloat(recipe.costprice) || 0  
        });
        setEditedItems(recipe.items.map(item => ({
            ...item,
            display_quantity: convertToDisplayUnit(item.quantity, item.uom, item.new_uom)
        })));

        if (!isRowExpanded(recipe.id)) {
            toggleRow(recipe.id);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditedRecipe(null);
        setEditedItems([]);

        if (editingId && isRowExpanded(editingId)) {
            toggleRow(editingId);
        }
    };

    const handleRecipeFieldChange = (field, value) => {
        setEditedRecipe(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleItemQuantityChange = (index, value) => {
        const newItems = [...editedItems];
        const item = newItems[index];

        item.display_quantity = value;

        const baseQuantity = convertToBaseUnit(value, item.uom, item.new_uom);
        item.quantity = baseQuantity;

        const unit = parseFloat(item.unit) || 1;
        const rate = parseFloat(item.rate) || 0;
        item.cost = (baseQuantity / unit) * rate;

        setEditedItems(newItems);

        const totalCost = newItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
        setEditedRecipe(prev => ({
            ...prev,
            costprice: totalCost
        }));
    };

    const handleSave = () => {
        if (!editedRecipe || !editingId) return;

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss('save-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                const itemsToSave = editedItems.map(item => ({
                                    ...item,
                                    quantity: item.quantity,
                                    display_quantity: undefined
                                }));

                                await axios.put(`${baseUrl}/sub-recipe/${editingId}`, {
                                    token: token,
                                    outlet: selectedOutlet,
                                    name: editedRecipe.name,
                                    uom: editedRecipe.uom,
                                    unit: editedRecipe.unit,
                                    costprice: editedRecipe.costprice,
                                    items: itemsToSave
                                });

                                fetchSubRecipes();
                                toast.success('Sub-recipe updated successfully');
                                setEditingId(null);
                                setEditedRecipe(null);
                                setEditedItems([]);
                            } catch (error) {
                                console.error('Error updating sub-recipe:', error);
                                toast.error('Failed to update sub-recipe');
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('save-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-subrecipe-confirmation'
            }
        );
    };

    const handleDelete = (id) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this sub-recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/sub-recipe/${id}`, {
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    data: {
                                        token: token,
                                        outlet: selectedOutlet
                                    }
                                });

                                setSubRecipes(subRecipes.filter(recipe => recipe.id !== id));
                                toast.success('Sub-recipe deleted successfully');
                            } catch (error) {
                                console.error('Error deleting sub-recipe:', error);
                                toast.error('Failed to delete sub-recipe');
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-subrecipe-confirmation'
            }
        );
    };

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto mt-5">
                    <div className="flex justify-center text-center mb-3">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <h1 className="text-xl font-bold mb-3">View Sub-Recipes</h1>

                    {loading ? (
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="rounded-md border overflow-x-auto mb-8">
                            <table className="min-w-full divide-y divide-gray-200 text-center">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th></th>
                                        <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Rate
                                        </th>
                                        <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Unit
                                        </th>
                                        <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            UOM
                                        </th>
                                        <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {subRecipes.map((recipe) => {
                                        const isExpanded = isRowExpanded(recipe.id);
                                        const isEditing = editingId === recipe.id;

                                        return (
                                            <>
                                                <tr
                                                    key={recipe.id}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td
                                                        className="py-4 px-3 text-base text-gray-600 cursor-pointer"
                                                        onClick={() => toggleRow(recipe.id)}
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(recipe.id);
                                                            }}
                                                            className="text-gray-600 hover:text-gray-700"
                                                        >
                                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                        </button>
                                                    </td>
                                                    <td
                                                        className="p-4 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer"
                                                        onClick={!isEditing && (() => toggleRow(recipe.id))}
                                                    >
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editedRecipe.name}
                                                                onChange={(e) => handleRecipeFieldChange('name', e.target.value)}
                                                                className="w-40 border rounded py-2 text-center"
                                                            />
                                                        ) : recipe.name}
                                                    </td>
                                                    <td
                                                        className="p-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                                                        onClick={!isEditing && (() => toggleRow(recipe.id))}
                                                    >
                                                        {isEditing ? (
                                                            <input
                                                                type="number"
                                                                value={editedRecipe.costprice.toFixed(2)}
                                                                onChange={(e) => handleRecipeFieldChange('costprice', e.target.value)}
                                                                className="w-28 border rounded py-2 text-center"
                                                            />
                                                        ) : recipe.costprice}
                                                    </td>
                                                    <td
                                                        className="p-4 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
                                                        onClick={!isEditing && (() => toggleRow(recipe.id))}
                                                    >
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editedRecipe.unit}
                                                                onChange={(e) => handleRecipeFieldChange('unit', e.target.value)}
                                                                className="w-28 border rounded py-2 text-center text-gray-900"
                                                            />
                                                        ) : recipe.unit || '-'}
                                                    </td>
                                                    <td
                                                        className="p-4 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
                                                        onClick={!isEditing && (() => toggleRow(recipe.id))}
                                                    >
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editedRecipe.uom}
                                                                onChange={(e) => handleRecipeFieldChange('uom', e.target.value)}
                                                                className="w-24 border rounded py-2 text-center text-gray-900"
                                                            />
                                                        ) : recipe.uom || '-'}
                                                    </td>
                                                    <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                        {isEditing ? (
                                                            <div className="flex justify-center space-x-2">
                                                                <button
                                                                    onClick={handleSave}
                                                                    className="h-8 w-8 p-0 border rounded flex items-center justify-center text-green-600 hover:bg-green-50"
                                                                >
                                                                    <FaSave className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    className="h-8 w-8 p-0 border rounded flex items-center justify-center text-red-600 hover:bg-red-50"
                                                                >
                                                                    <FaTimesCircle className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex justify-center space-x-2">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEdit(recipe);
                                                                    }}
                                                                    className="h-8 w-8 p-0 border rounded flex items-center justify-center text-blue-600 hover:bg-blue-50"
                                                                >
                                                                    <FaEdit className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(recipe.id);
                                                                    }}
                                                                    className="h-8 w-8 p-0 border rounded flex items-center justify-center text-red-600 hover:bg-red-50"
                                                                >
                                                                    <FaTrashAlt className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan={6} className="p-3">
                                                            <div className="rounded-md border overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-gray-200 text-center">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Ingredient
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Rate
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Unit
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Quantity
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                UOM
                                                                            </th>
                                                                            <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                                Cost
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {(isEditing ? editedItems : recipe.items).map((item, index) => (
                                                                            <tr key={index}>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-900">
                                                                                    {item.name}
                                                                                </td>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                                                    {item.rate}
                                                                                </td>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                                                    {item.unit} ({item.uom})
                                                                                </td>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="number"
                                                                                            value={item.display_quantity || convertToDisplayUnit(
                                                                                                item.quantity,
                                                                                                item.uom,
                                                                                                item.new_uom
                                                                                            )}
                                                                                            onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                                                                                            className="w-24 mx-auto border rounded py-2 text-center text-gray-900"
                                                                                        />
                                                                                    ) : convertToDisplayUnit(
                                                                                        item.quantity,
                                                                                        item.uom,
                                                                                        item.new_uom
                                                                                    )}
                                                                                </td>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-600">
                                                                                    {item.new_uom || item.uom}
                                                                                </td>
                                                                                <td className="p-4 whitespace-nowrap text-sm text-gray-900">
                                                                                    {Number(item.cost).toFixed(2)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && subRecipes.length === 0 && (
                        <div className="text-center text-gray-600 py-4">No sub-recipes found</div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ViewSubRecipe;